import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Gem, X, Send } from "lucide-react";
import { API } from "@/lib/api";

const SUGGESTIONS = [
  "What is polki jewellery?",
  "Help me choose a bridal set",
  "How do I care for diamonds?",
];

export const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  if (!sessionRef.current) {
    sessionRef.current =
      localStorage.getItem("merakee_session") ||
      (() => {
        const id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        localStorage.setItem("merakee_session", id);
        return id;
      })();
  }

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            acc += JSON.parse(payload).delta;
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", content: acc };
              return next;
            });
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content: "I am having trouble connecting. Please try again, or book a consultation and we will assist you personally.",
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        data-testid="ai-assistant-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Chat with Meera, our concierge"
        className="fixed bottom-24 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full border border-[#A58343] bg-[#FAF7F1] text-[#A58343] shadow-[0_0_30px_rgba(197,162,77,0.25)] transition-colors duration-300 hover:bg-[#A58343] hover:text-[#262019] sm:bottom-6"
      >
        {!open && (
          <span className="absolute inset-0 animate-ping rounded-full border border-[#A58343]/50" />
        )}
        {open ? <X size={22} strokeWidth={1.5} /> : <Gem size={22} strokeWidth={1.5} />}
      </button>

      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="fixed bottom-24 right-6 z-50 flex h-[540px] max-h-[72vh] w-[380px] max-w-[calc(100vw-3rem)] flex-col border border-[#A58343]/30 bg-[#FFFFFF] shadow-2xl"
        >
          <div className="border-b border-[#A58343]/20 bg-[#EFE8DC] px-6 py-4">
            <p className="font-serif text-lg tracking-wide text-[#262019]">Meera</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#A58343]">
              Merakee Concierge
            </p>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-5 overflow-y-auto px-5 py-6" data-testid="ai-assistant-messages">
            {messages.length === 0 && (
              <div>
                <p className="font-serif text-lg leading-relaxed text-[#262019]">
                  Namaste. I am Meera, your personal guide to Merakee. Ask me anything about
                  our diamonds, or let me help you find the perfect piece.
                </p>
                <div className="mt-5 flex flex-col gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`ai-suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="border border-[#A58343]/25 px-4 py-2.5 text-left text-xs tracking-wide text-[#A58343] transition-colors duration-300 hover:bg-[#A58343]/10"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "text-right" : ""}>
                <div
                  className={`inline-block max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#A58343] text-[#262019]"
                      : "border border-[#A58343]/20 bg-[#EFE8DC]/40 text-[#262019]"
                  }`}
                >
                  {m.content || (streaming && m.role === "assistant" ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-[#A58343]/20 p-4">
            <div className="flex items-center gap-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask Meera anything…"
                data-testid="ai-assistant-input"
                className="field !py-2 text-base"
              />
              <button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                data-testid="ai-assistant-send"
                aria-label="Send message"
                className="shrink-0 text-[#A58343] transition-colors duration-300 hover:text-[#8C6D33] disabled:opacity-40"
              >
                <Send size={18} strokeWidth={1.5} />
              </button>
            </div>
            <p className="mt-3 text-center text-[10px] tracking-[0.2em] text-[#7C7163]">
              READY TO BUY?{" "}
              <Link to="/consultation" data-testid="ai-assistant-consult-link" className="text-[#A58343] hover:text-[#8C6D33]">
                BOOK A CONSULTATION
              </Link>
            </p>
          </div>
        </div>
      )}
    </>
  );
};
